/* eslint-disable no-nested-ternary */
import { convertHeicHeifToPng, useModal } from '@application/utils';
import { ErrorText, ImageCropperModal, Loading } from '@presentation/components';
import { hasFieldError } from '@presentation/utils';
import { useField } from 'formik';
import type { FC, MouseEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { FaCamera, FaTrashAlt } from 'react-icons/fa';
import * as Styled from './styled';
import type { InputFileFieldProps } from './types';

export * from './types';

export const InputFileField: FC<InputFileFieldProps> = (props) => {
  const {
    name,
    disabled = false,
    accept,
    iconOnly = false,
    isImage = true,
    icon,
    placeholder,
    multiple = false,
    imageCropperProps,
    log,
    ...rest
  } = props;

  const [{ value, onBlur }, meta, helpers] = useField<string | File | File[] | undefined>(name);
  const [tempFile, setTempFile] = useState<File | undefined>(); // EXCEPTIONAL USE OF STATE IN PRESENTATION, THIS COMPONENT IS USED TOO MUCH AND TOO DEEPLY NESTED.
  const [fileNames, setFileNames] = useState<string[]>([]); // EXCEPTIONAL USE OF STATE IN PRESENTATION, THIS COMPONENT IS USED TOO MUCH AND TOO DEEPLY NESTED.
  const [loading, setLoading] = useState<boolean>(false);
  const uniquePrefix = useMemo(() => Math.random().toString(36).substring(2, 7), []);
  const { modal, show, close } = useModal();
  /**  Exceptional use of useModal in presentaion layer.
   * This component is used too much, and nested way to deeply.
   * Also, I consider it a extension of the the native input api.
   */

  const cropperSaveHandler = useCallback(
    (croppedFile: File) => {
      close();
      imageCropperProps?.onSave?.(croppedFile);
      const { name: fileName, type, size } = croppedFile;
      log?.({
        content: `croppedFile: ${fileName} ${size} ${type}`,
        params: { fileName: croppedFile.name, size: croppedFile.size, type: croppedFile.type },
      });
      return helpers.setValue(croppedFile);
    },
    [close, helpers, imageCropperProps, log],
  );

  const clearClickHandler = useCallback(
    (event: MouseEvent<HTMLDivElement>, index: number) => {
      event.preventDefault();
      event.stopPropagation();
      return helpers.setValue(undefined);
    },
    [helpers],
  );

  const src = useMemo(() => {
    let returnSrc = '';
    if (typeof value === 'string') returnSrc = value;
    else if (typeof window !== 'undefined' && value instanceof File)
      returnSrc = URL.createObjectURL(value);
    return returnSrc;
  }, [value]);

  const imageChangeHandler = useCallback(
    async (eventFiles: FileList) => {
      const { name: fileName, size, type } = eventFiles[0];
      log?.({
        content: `InputFileField : Uploaded image ${fileName} - ${size} - ${type}`,
        params: { fileName, size, type },
      });

      setTempFile(eventFiles[0]);
      show();
    },
    [log, show],
  );

  const multipleFilesHandler = useCallback(
    async (eventFiles: FileList) => {
      const files: File[] = [];
      for (let i = 0; i <= eventFiles.length; i += 1) {
        const file = eventFiles.item(i);
        if (file) {
          if (file.type.includes('heic') || file.type.includes('heif')) {
            log?.({
              content: `Converting original ${file.type} to png ${file.name} ${file.size} ${file.type}`,
              params: { name: file.name, size: file.size, type: file.type },
            });

            // eslint-disable-next-line no-await-in-loop
            const convertedFile = await convertHeicHeifToPng(file);
            files.push(convertedFile);
          } else {
            files.push(file);
          }
        }
      }
      setFileNames(files.map((item) => item.name));

      const logText = files
        .map((item) => {
          const { name: fileName, size, type } = item;
          return `${fileName} - ${size} - ${type}`;
        })
        .join(', ');

      log?.({
        content: `InputFileField : Uploaded multiple files ${logText}`,
        params: {
          files: files.map((item) => ({ name: item.name, size: item.size, type: item.type })),
        },
      });

      helpers.setValue(files);
    },
    [helpers, log],
  );

  const otherFileHandler = useCallback(
    async (eventFiles: FileList) => {
      const file = eventFiles[0];
      if (file.type.includes('heic') || file.type.includes('heif')) {
        // eslint-disable-next-line no-await-in-loop
        const convertedFile = await convertHeicHeifToPng(file);
        helpers.setValue(convertedFile);
      } else {
        helpers.setValue(eventFiles[0]);
      }
      log?.({
        content: `InputFileField : Uploaded (other)file ${file.name} - ${file.size} - ${file.type}`,
        params: { fileName: file.name, size: file.size, type: file.type },
      });
    },
    [helpers, log],
  );

  const changeHandler = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files: eventFiles } = event.currentTarget;
      if (!eventFiles || !eventFiles?.[0]) return;
      setLoading(true);
      if (isImage) {
        await imageChangeHandler(eventFiles);
      } else if (multiple) {
        await multipleFilesHandler(eventFiles);
      } else {
        await otherFileHandler(eventFiles);
      }
      setLoading(false);
    },
    [imageChangeHandler, isImage, multiple, multipleFilesHandler, otherFileHandler],
  );

  const clickHandler: React.MouseEventHandler<HTMLInputElement> = useCallback((event) => {
    // eslint-disable-next-line no-param-reassign
    (event.target as any).value = null;
  }, []);

  return (
    <>
      {isImage && (
        <ImageCropperModal
          modal={modal}
          {...imageCropperProps}
          onSave={cropperSaveHandler}
          file={tempFile}
          log={log}
        />
      )}
      {iconOnly ? (
        <>
          <Styled.Input
            disabled={disabled}
            type="file"
            multiple={multiple}
            id={`${uniquePrefix}-input-file`}
            name={name}
            accept={accept}
            {...rest}
            onChange={changeHandler}
            onClick={clickHandler}
          />
          <Styled.IconOnlyLabel
            htmlFor={`${uniquePrefix}-input-file`}
            disabled={disabled}
            hasError={hasFieldError(meta)}
          >
            {icon}
            {!!placeholder && <Styled.Subtitle>{placeholder}</Styled.Subtitle>}
          </Styled.IconOnlyLabel>
        </>
      ) : (
        <>
          {!!value && isImage ? (
            <>
              <Styled.MainImage src={src}>
                {!disabled && (
                  <Styled.Overlay onClick={(event) => clearClickHandler(event, 0)}>
                    <FaTrashAlt />
                  </Styled.Overlay>
                )}
              </Styled.MainImage>
            </>
          ) : (
            <>
              <Styled.Input
                disabled={disabled}
                type="file"
                multiple={multiple}
                id={`${uniquePrefix}-input-file`}
                name={name}
                accept={accept}
                onBlur={onBlur}
                {...rest}
                onChange={changeHandler}
                onClick={clickHandler}
              />
              <Styled.Label
                htmlFor={`${uniquePrefix}-input-file`}
                disabled={disabled}
                hasError={!!meta.error && !!meta.touched}
              >
                <Styled.IconWrapper>{icon ?? <FaCamera />}</Styled.IconWrapper>
                {!!placeholder && <Styled.Subtitle>{placeholder}</Styled.Subtitle>}
                {loading ? (
                  <Loading css={Styled.FileLoadingStyle} color="dark" />
                ) : multiple ? (
                  <>
                    {fileNames.length && !isImage
                      ? fileNames.map((item, key) => (
                          <>
                            {item}
                            <br />
                          </>
                        ))
                      : null}
                  </>
                ) : (
                  <>
                    {value != null && !isImage && typeof value !== 'string' ? (
                      <>{value?.name}</>
                    ) : null}
                  </>
                )}
              </Styled.Label>
            </>
          )}
        </>
      )}
      {/* On public profile when there are multiple images, the error messages displaying as C then h... */}
      {/* can't figure out a proper fix, only a workaround */}
      {!!meta.error && meta.error.length > 1 ? <ErrorText>{meta.error}</ErrorText> : null}
    </>
  );
};
